import React, { useState, useEffect } from "react";
import { MantineProvider, ColorSchemeProvider } from "@mantine/core";
import { Routes, Route } from "react-router-dom";
import { SpotlightProvider } from "@mantine/spotlight";
import Layout from "./pages/layout";
import { authenticatedRouteElements } from "./routes/appRoutes";
import { TAB_NAV_ENABLED } from "./utils/tabNavigation";
import Login from "./pages/login";
import ForgetPassword from "./pages/forgetPassword";
import Health from "./pages/health";
import { NotificationsProvider } from "@mantine/notifications";
import { AuthProvider, RequireAuth } from "react-auth-kit";
import { theme } from "./theme";
import socketIO from "socket.io-client";
import { createContext } from "react";
import { IconSearch } from "@tabler/icons";
import { useSpotLight } from "./hooks/useSpotLight";
import { LanguageProvider } from "./context/language";

const socket = socketIO.connect(`${process.env.REACT_APP_SOCKET_URL}`);
export const SocketContext = createContext(null);

export const authName = "_mbid";
const languageFromLocalStorage = localStorage.getItem("language") || "en";

const defaultColorScheme = localStorage.getItem(authName + "color-scheme");

function App() {
  const [colorScheme, setColorScheme] = useState(defaultColorScheme ?? "dark");

  const toggleColorScheme = (value) => {
    setColorScheme(value || (colorScheme === "dark" ? "light" : "dark"));
    localStorage.setItem(authName + "color-scheme", value);
  };

  const [onSpotLightQueryChange, spotLightActions] = useSpotLight();

  useEffect(() => {
    document.title = `${process.env?.REACT_APP_WEB_TITLE}` ?? "";

    window.addEventListener("error", (e) => {
      console.log(e);
      if (e.message === "ResizeObserver loop limit exceeded") {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div",
        );
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay",
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute("style", "display: none");
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute("style", "display: none");
        }
      }
      e.stopImmediatePropagation("ResizeObserver loop limit exceeded");
      e.preventDefault();
    });
  }, []);

  return (
    <SocketContext.Provider value={socket}>
      <LanguageProvider initialLanguage={languageFromLocalStorage}>
        <AuthProvider authType={"localstorage"} authName={authName}>
          <ColorSchemeProvider
            colorScheme={colorScheme}
            toggleColorScheme={toggleColorScheme}
          >
            <MantineProvider
              inherit
              theme={{
                colorScheme,
                ...theme,
              }}
              withGlobalStyles
              withNormalizeCSS
            >
              <NotificationsProvider zIndex={2077}>
                <SpotlightProvider
                  actions={spotLightActions}
                  searchIcon={<IconSearch size="1.2rem" />}
                  searchPlaceholder="Search..."
                  shortcut="mod + 1"
                  nothingFoundMessage="No Results"
                  onQueryChange={onSpotLightQueryChange}
                  limit={30}
                >
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route
                      path="/forgetPassword"
                      element={<ForgetPassword />}
                    />
                    <Route path="/health" element={<Health />} />

                    {TAB_NAV_ENABLED ? (
                      <Route
                        path="/*"
                        element={
                          <RequireAuth loginPath="/login">
                            <Layout />
                          </RequireAuth>
                        }
                      />
                    ) : (
                      <Route
                        path="/"
                        element={
                          <RequireAuth loginPath="/login">
                            <Layout />
                          </RequireAuth>
                        }
                      >
                        {authenticatedRouteElements()}
                      </Route>
                    )}
                  </Routes>
                </SpotlightProvider>
              </NotificationsProvider>
            </MantineProvider>
          </ColorSchemeProvider>
        </AuthProvider>
      </LanguageProvider>
    </SocketContext.Provider>
  );
}

export default App;
